import { useEffect, useState } from "react";
import { Users, Clock, HeartHandshake, Shield, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import HUDFrame from "@/components/HUDFrame";

const dayLabel = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

export default function Hangar() {
  const [profile, setProfile] = useState(null);
  const [signals, setSignals] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/profile"), api.get("/signals")])
      .then(([p, s]) => {
        if (cancelled) return;
        setProfile(p.data);
        setSignals(s.data);
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Hangar unavailable."));
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) return <div className="mx-auto max-w-3xl px-6 py-16 text-mist" data-testid="hangar-error">{String(err)}</div>;
  if (!profile || !signals)
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist" data-testid="hangar-loading">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Inventorying resources…
      </div>
    );

  const totalHours = profile.members.reduce((a, m) => a + (m.weekly_hours_available || 0), 0);
  const usedPct = Math.round((signals.committed_hours / (signals.available_hours || 1)) * 100);
  const remaining = Math.max(0, signals.available_hours - signals.committed_hours);

  return (
    <div data-testid="hangar-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">H-04 · Hangar</p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        What holds
        <br />
        <span className="italic text-mist">the household aloft.</span>
      </h1>
      <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">
        A live inventory from your onboarding — the people, time, and defended
        room your tower reads to keep you calm.
      </p>

      {/* Overview */}
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <HUDFrame className="bg-deep/40 p-6" tone="teal">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Users className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Roster
          </div>
          <div className="mt-4 font-mono text-4xl text-signalGreen">
            {profile.members.length}
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            member{profile.members.length === 1 ? "" : "s"} · {totalHours}h/wk
          </p>
        </HUDFrame>

        <HUDFrame className="bg-deep/40 p-6" tone={usedPct > 80 ? "amber" : "teal"}>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Clock className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Discretionary time
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-mono text-4xl text-signalAmber">{remaining.toFixed(0)}</span>
            <span className="font-mono text-sm text-mist">h remaining / wk</span>
          </div>
          <div className="mt-4 h-1.5 w-full bg-rule/60">
            <div className={`h-full ${usedPct > 80 ? "bg-signalRed" : usedPct > 50 ? "bg-signalAmber" : "bg-signalGreen"}`} style={{ width: `${Math.min(100, usedPct)}%` }} />
          </div>
        </HUDFrame>

        <HUDFrame className="bg-deep/40 p-6" tone={signals.recovery_status === "red" ? "amber" : "teal"}>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Shield className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Defended reserve
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-mono text-4xl text-signalGreen">{signals.protected_hours}</span>
            <span className="font-mono text-sm text-mist">h protected</span>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-mist">
            {profile.recovery_windows.length} window{profile.recovery_windows.length === 1 ? "" : "s"} defended.
          </p>
        </HUDFrame>
      </div>

      {/* Members list */}
      <HUDFrame className="mt-10 bg-deep/40 p-8" tone="teal">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          <HeartHandshake className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
          Members
        </div>
        <ul className="mt-6 divide-y divide-rule" data-testid="hangar-members">
          {profile.members.map((m) => (
            <li key={m.member_id} className="flex items-center gap-4 py-4">
              <div className="flex h-9 w-9 items-center justify-center border border-rule bg-obsidian font-mono text-[11px] text-teal">
                {(m.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-ink">{m.name}</div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                  {m.role}
                </div>
              </div>
              <div className="font-mono text-sm text-ink">{m.weekly_hours_available}h/wk</div>
            </li>
          ))}
        </ul>
      </HUDFrame>

      {/* Commitments summary */}
      <HUDFrame className="mt-6 bg-deep/40 p-8" tone="teal">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          Standing commitments · {profile.commitments.length}
        </div>
        {profile.commitments.length === 0 ? (
          <p className="mt-4 text-sm text-mist">No standing commitments logged yet. Add some in onboarding.</p>
        ) : (
          <ul className="mt-4 divide-y divide-rule" data-testid="hangar-commitments">
            {profile.commitments.map((c) => (
              <li key={c.commitment_id} className="flex flex-wrap items-baseline gap-4 py-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-teal">
                  {c.category}
                </span>
                <span className="text-ink">{c.title || "(untitled)"}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                  {c.days.map((d) => dayLabel[d]).join(" · ") || "—"}
                </span>
                <span className="ml-auto font-mono text-xs text-mist">
                  {c.duration_min}m
                </span>
              </li>
            ))}
          </ul>
        )}
      </HUDFrame>
    </div>
  );
}
